import * as React from "react";
import { observer } from "mobx-react";
import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  type MRT_ColumnDef,
  type MRT_Row,
} from "material-react-table";
import { Box, Typography, MenuItem, ListItemIcon, Button, IconButton, Tooltip, Dialog, DialogActions, DialogContent } from "@mui/material";
import { AddIcon, DeleteIcon, EditIcon, FullscreenExitIcon, FullscreenIcon, GroupWorkIcon, RestoreFromTrashIcon as RestoreIcon } from "../../../icons";
import { CrudRow, RowCrudStatus, originalDeletedStatusList, FooterData, GridColumn, GridChangeSet } from "../types";
import { IMenuAction } from "../../../common/controls";
import { useFileCellAffordances } from "./fileCellAffordances";
import { graphqlToDateTime } from "../../../common/utils";
import { diaDeCalendario, formatearDia } from "../../../common/calendarDate";
import { useRootStore } from "../../../store/contexts";
import { TextField, Checkbox } from "@mui/material";
import { Theme, alpha, useTheme } from "@mui/material/styles";
import { NumericFormat } from "react-number-format";
import { InputReference } from "./InputReference";
import { InputReferenceSet } from "./InputReferenceSet";
import { InputIntegerChoices } from "./InputIntegerChoices";
import { FieldLabelWithHelp } from "./FieldHelpIcon";

type ColumnFieldType = "String" | "Int" | "Decimal" | "Boolean" | "Date" | "DateTime" | "__Reference" | "File" | "__Enum";

type ColumnMetadata = {
  fieldType: ColumnFieldType;
  nullable?: boolean;
  readOnly?: boolean;
  multi?: boolean;
  onChange?: (value: string) => void;
  onSelect?: (value: any) => void;
  getOptions?: () => {displayId: string, id: string}[];
  onView?: ((id: string) => void) | (() => void);
  // integer con .choices(unit=…): la edición inline usa el mismo dropdown
  // discreto [min..max] que el form (InputIntegerChoices), no un input numérico.
  choices?: {
    min: number;
    max: number;
    unitSingular?: string;
    unitPlural?: string;
    nullText?: string;
  };
};

/** Etiqueta amigable de un integer con choices: "N {unidad}" (singular/plural)
 * o el nullText cuando está vacío. Espeja InputIntegerChoices/ShowIntegerChoices. */
const formatChoiceLabel = (
  value: any,
  choices: { unitSingular?: string; unitPlural?: string; nullText?: string }
): string => {
  if (value === undefined || value === null || value === "") return choices.nullText ?? "";
  const unit = Number(value) === 1 ? choices.unitSingular : choices.unitPlural;
  return unit ? `${value} ${unit}` : `${value}`;
};

type ItemsCRUDCompactProps = {
  label: string;
  rows: CrudRow[];
  columns: GridColumn[];
  columnTypes?: Record<string, ColumnMetadata>;
  onCommitChanges: (changes: GridChangeSet) => void;
  onCommitInlineChange?: (rowId: string, changes: Record<string, any>) => void;
  validate?: (changes: GridChangeSet) => boolean;
  onRestore: (id: string) => void;
  menuActions?: IMenuAction[];
  showLabel?: boolean;
  maxRows?: number;
  canCreate?: boolean;
  popupComponent?: (row: any) => React.ReactElement;
  // Ancho del modal del form de sub-entidad. Default 'xl' (sin regresión).
  popupMaxWidth?: "sm" | "md" | "lg" | "xl";
  dummyCRUDRestoredObservable: number;
  footerData?: FooterData;
  error?: string;
  helperText?: string;
  isEditVisible?: (row: any) => boolean;
  isDeleteVisible?: (row: any) => boolean;
  disabled?: boolean;
  hidden?: boolean;
  description?: string;
  // Tree display props
  enableExpanding?: boolean;
  renderDetailPanel?: (props: { row: MRT_Row<any> }) => React.ReactNode;
  // Grouping props
  groupBy?: string;
  groupColumnLabel?: string;
  // File-cell affordances (download + view PDF / Excel / image). Same contract
  // as ShowItemsCompact: `entityName` resolves the live download endpoint;
  // when both `snapshotEntityName` and `snapshotRecordId` are set the
  // snapshot endpoint is used and the file is addressed by its uuid sidecar.
  enableDownload?: boolean;
  entityName?: string;
  snapshotEntityName?: string;
  snapshotRecordId?: string;
};

/**
 * El color de fondo de una fila segun su estado de edicion.
 *
 * Recibe el tema en vez de leerlo: es una funcion suelta, fuera de todo
 * componente, asi que no puede usar el hook. Pasarlo explicito la deja pura y
 * comprobable.
 */
const getRowBgColor = (status: RowCrudStatus, theme: Theme): string => {
  if (status === "added") return alpha(theme.palette.success.main, 0.22);
  if (["original-deleted", "original-updated-deleted", "added-deleted"].includes(status))
    return alpha(theme.palette.error.main, 0.22);
  if (["original-updated", "added-updated"].includes(status))
    return alpha(theme.palette.warning.main, 0.22);
  return "";
};

export const ItemsCRUDCompact: React.FC<ItemsCRUDCompactProps> = observer(
  ({
    label,
    rows,
    columns,
    columnTypes = {},
    onCommitChanges,
    onCommitInlineChange,
    validate,
    onRestore,
    menuActions = [],
    showLabel = true,
    maxRows,
    canCreate = true,
    popupComponent,
    popupMaxWidth = "xl",
    dummyCRUDRestoredObservable,
    footerData,
    error,
    helperText,
    isEditVisible,
    isDeleteVisible,
    disabled = false,
    hidden = false,
    description,
    enableExpanding,
    renderDetailPanel,
    groupBy,
    groupColumnLabel,
    enableDownload = false,
    entityName,
    snapshotEntityName,
    snapshotRecordId,
  }) => {
  const theme = useTheme();
    if (hidden) return null;
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    const { renderFileCell, excelViewerElement } = useFileCellAffordances({
      entityName,
      snapshotEntityName,
      snapshotRecordId,
      enableDownload,
    });

    const [editingRow, setEditingRow] = React.useState<CrudRow | null>(null);
    const [isPopupOpen, setIsPopupOpen] = React.useState(false);
    const [isAddingNew, setIsAddingNew] = React.useState(false);
    const [isFullScreen, setIsFullScreen] = React.useState(false);

    const [editingCells, setEditingCells] = React.useState<Set<string>>(new Set());
    const [editingValues, setEditingValues] = React.useState<Map<string, any>>(new Map());

    // Estado controlado para grouping
    const [currentGrouping, setCurrentGrouping] = React.useState<string[]>(groupBy ? [groupBy] : []);
    const isGroupingRemoved = groupBy && currentGrouping.length === 0;

    const gridRows = React.useMemo(() => {
      return Array.isArray(rows) ? rows.slice() : Object.values(rows);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- dummyCRUDRestoredObservable is a sentinel observable used solely to force re-evaluation on CRUD ops; ESLint can't see it's intentional
    }, [rows, dummyCRUDRestoredObservable]);

    React.useEffect(() => {
      setEditingValues(prev => {
        const newMap = new Map(prev);
        let hasChanges = false;

        newMap.forEach((value, key) => {
          const [rowId, columnName] = key.split('__');
          const row = gridRows.find((r: any) => r.id === rowId) as any;
          if (row && row[columnName] === value) {
            newMap.delete(key);
            hasChanges = true;
          }
        });

        return hasChanges ? newMap : prev;
      });
    }, [gridRows]);

    const renderEditableCell = (row: CrudRow, columnName: string, currentValue: any, metadata?: ColumnMetadata) => {
      if (!metadata || metadata.readOnly) {
        if (metadata?.choices) return formatChoiceLabel(currentValue, metadata.choices);
        return currentValue;
      }

      const isEditing = isCellEditing(row.id, columnName);
      const isDeleted = originalDeletedStatusList.includes(row.__status__);

      if (isDeleted) {
        return metadata.choices ? formatChoiceLabel(currentValue, metadata.choices) : currentValue;
      }

      // integer con .choices: dropdown discreto al editar, label amigable al mostrar.
      if (metadata.choices) {
        if (isEditing) {
          // El wrapper stopPropagation evita que el click de abrir/seleccionar el
          // dropdown burbujee al onClick de la fila (que abriría el modal de edición).
          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <InputIntegerChoices
                label=""
                value={currentValue ?? undefined}
                min={metadata.choices.min}
                max={metadata.choices.max}
                unitSingular={metadata.choices.unitSingular}
                unitPlural={metadata.choices.unitPlural}
                nullable={metadata.nullable}
                nullText={metadata.choices.nullText}
                autoFocus
                customWidth={150}
                onChange={(value) => {
                  setEditingCells((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(getCellKey(row.id, columnName));
                    return newSet;
                  });
                  // Vía canónica de commit inline (mergea el valor en la fila);
                  // onCommitChanges lee del update-store del modal y no aplica acá.
                  if (onCommitInlineChange) {
                    onCommitInlineChange(row.id, { [columnName]: value });
                  } else {
                    onCommitChanges({ changed: { [row.id]: { [columnName]: value } } });
                  }
                }}
              />
            </Box>
          );
        }
        return (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              startCellEdit(row.id, columnName, currentValue);
            }}
            sx={{ cursor: 'pointer', minHeight: '40px', width: '100%', padding: '8px', display: 'flex', alignItems: 'center', '&:hover': { backgroundColor: 'action.hover' } }}
          >
            {formatChoiceLabel(currentValue, metadata.choices) || ' '}
          </Box>
        );
      }

      if (metadata.fieldType === "String") {
        if (isEditing) {
          const editingValue = getEditingValue(row.id, columnName, currentValue);
          return (
            <TextField
              size="small"
              value={editingValue || ""}
              onChange={(e) => updateEditingValue(row.id, columnName, e.target.value)}
              onBlur={() => commitAndStopCellEdit(row.id, columnName)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitAndStopCellEdit(row.id, columnName);
                } else if (['ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.stopPropagation();
                }
              }}
              autoFocus
              fullWidth
              sx={{ m: 0 }}
            />
          );
        }
        const displayValue = getEditingValue(row.id, columnName, currentValue);
        return (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              startCellEdit(row.id, columnName, displayValue);
            }}
            sx={{ cursor: 'pointer', minHeight: '40px', width: '100%', padding: '8px', display: 'flex', alignItems: 'center', '&:hover': { backgroundColor: 'action.hover' } }}
          >
            {displayValue || '\u00A0'}
          </Box>
        );
      }

      if (metadata.fieldType === "Int" || metadata.fieldType === "Decimal") {
        if (isEditing) {
          const editingValue = getEditingValue(row.id, columnName, currentValue);
          return (
            <NumericFormat
              size="small"
              value={editingValue}
              customInput={TextField}
              onValueChange={(values) => {
                if (metadata.nullable && values.floatValue === undefined) {
                  updateEditingValue(row.id, columnName, undefined);
                } else if (values.floatValue !== undefined) {
                  updateEditingValue(row.id, columnName, values.floatValue);
                }
              }}
              onBlur={() => commitAndStopCellEdit(row.id, columnName)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitAndStopCellEdit(row.id, columnName);
                } else if (['ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.stopPropagation();
                }
              }}
              autoFocus
              fullWidth
              allowNegative={true}
              allowedDecimalSeparators={['.', ',']}
              decimalScale={metadata.fieldType === "Decimal" ? 2 : 0}
              sx={{ m: 0 }}
            />
          );
        }
        const displayValue = getEditingValue(row.id, columnName, currentValue);
        return (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              startCellEdit(row.id, columnName, displayValue);
            }}
            sx={{ cursor: 'pointer', minHeight: '40px', width: '100%', padding: '8px', display: 'flex', alignItems: 'center', '&:hover': { backgroundColor: 'action.hover' } }}
          >
            {displayValue !== undefined && displayValue !== null ? displayValue : '\u00A0'}
          </Box>
        );
      }

      if (metadata.fieldType === "Boolean") {
        return (
          <Checkbox
            checked={Boolean(currentValue)}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              const changes: GridChangeSet = {
                changed: {
                  [row.id]: { [columnName]: e.target.checked }
                }
              };
              onCommitChanges(changes);
            }}
            sx={{ p: 0 }}
          />
        );
      }

      if (metadata.fieldType === "__Reference") {
        const fieldNameWithoutPrefix = columnName.startsWith('__') ? columnName.substring(2) : columnName;
        const showFieldName = 'show' + fieldNameWithoutPrefix.charAt(0).toUpperCase() + fieldNameWithoutPrefix.slice(1);
        const referenceValue = (row as any)[showFieldName];

        if (isEditing) {
          const options = metadata.getOptions ? metadata.getOptions() : [];

          if (metadata.multi) {
            return (
              <InputReferenceSet
                id={columnName}
                selectedValues={referenceValue || []}
                onSelect={(values: {displayId: string, id: string}[]) => {
                  const changes: Record<string, any> = {
                    [showFieldName]: values,
                    [columnName]: values.map(v => v.displayId)
                  };
                  if (onCommitInlineChange) {
                    onCommitInlineChange(row.id, changes);
                  }
                  setEditingCells(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(getCellKey(row.id, columnName));
                    return newSet;
                  });
                }}
                onChange={(value: string) => metadata.onChange?.(value)}
                options={options}
                customWidth={undefined}
                autoFocus
              />
            );
          } else {
            return (
              <InputReference
                id={columnName}
                selectedValue={referenceValue}
                onSelect={(value: {displayId: string, id: string} | null) => {
                  const changes: Record<string, any> = {
                    [showFieldName]: value,
                    [columnName]: value?.displayId || ""
                  };
                  if (onCommitInlineChange) {
                    onCommitInlineChange(row.id, changes);
                  }
                  setEditingCells(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(getCellKey(row.id, columnName));
                    return newSet;
                  });
                }}
                onChange={(value: string) => metadata.onChange?.(value)}
                options={options}
                customWidth={undefined}
                autoFocus
              />
            );
          }
        }

        const displayValue = metadata.multi
          ? (referenceValue || []).map((v: any) => v.displayId).join(", ")
          : (referenceValue?.displayId || currentValue || "");

        return (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              startCellEdit(row.id, columnName, referenceValue);
            }}
            sx={{ cursor: 'pointer', minHeight: '40px', width: '100%', padding: '8px', display: 'flex', alignItems: 'center', '&:hover': { backgroundColor: 'action.hover' } }}
          >
            {displayValue || '\u00A0'}
          </Box>
        );
      }

      return currentValue;
    };

    const mrtColumns = React.useMemo(() => {
      const baseColumns = columns.map((col) => {
        const colMeta = columnTypes[col.name];
        const isNumeric = colMeta && ["Decimal", "Int"].includes(colMeta.fieldType);
        // Todas las columnas crecen para repartir el espacio sobrante proporcionalmente
        // al tamaño base. Si solo una columna tuviese grow=true, esa columna se quedaría
        // con TODO el espacio (ej. "Editorial" enorme, "Porcentaje" truncado).
        const baseColumn = {
          accessorKey: col.name,
          header: col.title || col.name,
          size: isNumeric ? 110 : 150,
          grow: true,
          ...(isNumeric && {
            muiTableHeadCellProps: { sx: { textAlign: "center" } },
            muiTableBodyCellProps: { sx: { textAlign: "center" } },
          }),
        };

        const fileDataKey = `${col.name}_data`;
        const columnMetadata = columnTypes[col.name];
        const footerCell = footerData?.[col.name];

        return {
          ...baseColumn,
          Footer: footerCell ? () => (
            <Box sx={{ fontWeight: 500, ...footerCell.sx }}>
              {footerCell.value}
            </Box>
          ) : undefined,
          Cell: ({ row, cell }: any) => {
            const fileData = row.original[fileDataKey];
            if (fileData?.mimetype) {
              return renderFileCell(row.original, col.name, fileData.mimetype, cell.getValue());
            }

            if (columnMetadata && ["String", "Int", "Decimal", "Boolean", "__Reference"].includes(columnMetadata.fieldType)) {
              return renderEditableCell(row.original, col.name, cell.getValue(), columnMetadata);
            }

            const cellValue = cell.getValue();
            // Un date (día de calendario, "AAAA-MM-DD"), por su tipo declarado (la metadata
            // de la columna o la columna): el formateador del idioma, sin la zona del
            // navegador. Un Date no es renderizable como children de React: sin ese tipo,
            // es un instante (un dateTime) y va con su hora.
            if ((columnMetadata?.fieldType ?? col.fieldType) === "Date") {
              return formatearDia(diaDeCalendario(cellValue), langStore.timeFormat);
            }
            if (cellValue instanceof Date) {
              return graphqlToDateTime(cellValue.toISOString());
            }
            return cellValue;
          },
        };
      }) as MRT_ColumnDef<any>[];

      // Add groupBy column if specified and not already in columns
      if (groupBy && !columns.find(c => c.name === groupBy)) {
        baseColumns.unshift({
          accessorKey: groupBy,
          header: groupColumnLabel || groupBy,
          size: 200,
          grow: true,
        } as MRT_ColumnDef<any>);
      }

      return baseColumns;
      // eslint-disable-next-line react-hooks/exhaustive-deps -- renderEditableCell is an inline render function recreated each render; adding it would defeat the useMemo. Cells re-render anyway when editingCells/editingValues mutate.
    }, [columns, columnTypes, editingCells, editingValues, footerData, groupBy, groupColumnLabel, renderFileCell, langStore]);

    const handleAddRow = () => {
      const newRow: CrudRow = {
        id: `temp_${Date.now()}`,
        __status__: "added" as RowCrudStatus,
      };

      columns.forEach((col) => {
        if (col.name !== "id" && col.name !== "__status__") {
          newRow[col.name] = "";
        }
      });

      setEditingRow(newRow);
      setIsAddingNew(true);
      setIsPopupOpen(true);
    };

    const handleEditRow = (rowId: string) => {
      const row = gridRows.find((r: any) => r.id === rowId) as CrudRow;
      if (row) {
        setEditingRow(row);
        setIsAddingNew(false);
        setIsPopupOpen(true);
      }
    };

    const handleDeleteRow = (rowId: string) => {
      const changes: GridChangeSet = {
        deleted: [rowId],
      };

      onCommitChanges(changes);
    };

    const handleApplyChanges = () => {
      if (isAddingNew) {
        const changes: GridChangeSet = {
          added: [editingRow as any],
        };

        if (validate) {
          if (!validate(changes)) return;
        }

        onCommitChanges(changes);
      } else {
        const changes: GridChangeSet = {
          changed: { [(editingRow as CrudRow).id]: {} },
        };

        if (validate) {
          if (!validate(changes)) return;
        }

        onCommitChanges(changes);
      }

      setIsPopupOpen(false);
      setEditingRow(null);
    };

    const handleCancelChanges = () => {
      setIsPopupOpen(false);
      setEditingRow(null);
    };

    const getCellKey = (rowId: string, columnName: string) => `${rowId}__${columnName}`;

    const isCellEditing = (rowId: string, columnName: string) => {
      return editingCells.has(getCellKey(rowId, columnName));
    };

    const startCellEdit = (rowId: string, columnName: string, initialValue: any) => {
      const cellKey = getCellKey(rowId, columnName);
      setEditingCells(prev => new Set(prev).add(cellKey));
      setEditingValues(prev => new Map(prev).set(cellKey, initialValue));
    };

    const updateEditingValue = (rowId: string, columnName: string, value: any) => {
      const cellKey = getCellKey(rowId, columnName);
      setEditingValues(prev => new Map(prev).set(cellKey, value));
    };

    const getEditingValue = (rowId: string, columnName: string, currentValue: any) => {
      const cellKey = getCellKey(rowId, columnName);
      return editingValues.has(cellKey) ? editingValues.get(cellKey) : currentValue;
    };

    const commitAndStopCellEdit = (rowId: string, columnName: string) => {
      const cellKey = getCellKey(rowId, columnName);
      const value = editingValues.get(cellKey);

      setEditingCells(prev => {
        const newSet = new Set(prev);
        newSet.delete(cellKey);
        return newSet;
      });

      if (value !== undefined) {
        if (onCommitInlineChange) {
          onCommitInlineChange(rowId, { [columnName]: value });
        } else {
          const changes: GridChangeSet = {
            changed: {
              [rowId]: { [columnName]: value }
            }
          };
          onCommitChanges(changes);
        }
      }
    };

    const canAddRow = canCreate && (!maxRows || gridRows.length < maxRows);

    const gridContent = (
        <MaterialReactTable
          layoutMode="grid"
          muiTableContainerProps={{ sx: { maxWidth: "100%", overflowX: "auto" } }}
          columns={mrtColumns}
          data={gridRows}
          enableEditing={false}
          enableRowActions
          positionActionsColumn="first"
          enableColumnActions={true}
          enableColumnFilters={false}
          enablePagination={false}
          enableSorting={true}
          enableHiding={true}
          enableColumnOrdering={true}
          enableBottomToolbar={false}
          enableTopToolbar={true}
          enableFullScreenToggle={false}
          enableGlobalFilter={false}
          enableDensityToggle={false}
          enableExpanding={enableExpanding}
          renderDetailPanel={renderDetailPanel}
          enableGrouping={true}
          groupedColumnMode="remove"
          enableExpandAll={false}
          state={{
            grouping: currentGrouping,
          }}
          onGroupingChange={setCurrentGrouping}
          initialState={{
            density: 'compact',
            expanded: true,
          }}
          displayColumnDefOptions={{
            'mrt-row-actions': {
              header: langStore.actionsLabel,
              size: 80,
              minSize: 80,
              maxSize: 80,
              grow: false,
            },
            'mrt-row-expand': {
              header: '',
              size: 40,
              minSize: 40,
              maxSize: 40,
              grow: false,
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              verticalAlign: 'middle',
            },
          }}
          muiTableBodyCellProps={({ row, column }) => ({
            sx: {
              ...(row.getIsGrouped() && {
                fontWeight: 600,
                backgroundColor: 'action.hover',
                // La etiqueta de grupo vive en la celda expand (groupedColumnMode="remove");
                // al estrechar la columna a 40px debe desbordar sobre las celdas vacías de la fila.
                ...(column.id === 'mrt-row-expand' && {
                  overflow: 'visible',
                  whiteSpace: 'nowrap',
                }),
              }),
            },
          })}
          localization={{ groupedBy: langStore.groupedByLabel }}
          renderTopToolbarCustomActions={() => (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {canAddRow && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddRow}
                  size="small"
                  disabled={disabled}
                >
                  {langStore.addCommand}
                </Button>
              )}
              {isGroupingRemoved && (
                <Button
                  variant="outlined"
                  startIcon={<GroupWorkIcon />}
                  onClick={() => setCurrentGrouping([groupBy!])}
                  size="small"
                >
                  {langStore.restoreGroupingCommand}
                </Button>
              )}
            </Box>
          )}
          renderToolbarInternalActions={({ table }) => (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {!isFullScreen && (
                <Tooltip title={langStore.maximizeCommand}>
                  <IconButton onClick={() => setIsFullScreen(true)} size="small" aria-label="maximize">
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {/* Botón default de mostrar/ocultar columnas (único action interno
                  activo acá). NO usar MRT_ToolbarInternalButtons: invoca
                  renderToolbarInternalActions → recursión infinita. */}
              <MRT_ShowHideColumnsButton table={table} />
            </Box>
          )}
          muiTableBodyRowProps={({ row }) => {
            // When a popupComponent is wired, clicking the row opens the
            // editor — same target as the Edit menu item. Grouped rows and
            // deleted rows are not interactive. Cells that handle their own
            // clicks (inline editors, the Download / View icons) call
            // e.stopPropagation so this handler doesn't override them.
            const rowEditable =
              !!popupComponent &&
              !row.getIsGrouped() &&
              !originalDeletedStatusList.includes(row.original.__status__) &&
              (isEditVisible ? isEditVisible(row.original) : true);
            return {
              sx: {
                backgroundColor: getRowBgColor(row.original.__status__, theme),
                cursor: rowEditable ? "pointer" : undefined,
              },
              ...(rowEditable && {
                onClick: () => handleEditRow(row.original.id),
              }),
            };
          }}
          renderRowActionMenuItems={({ row, closeMenu }) => {
            const rowData = row.original;
            const isDeleted = originalDeletedStatusList.includes(rowData.__status__);

            const showEdit = isEditVisible ? isEditVisible(rowData) : true;
            const showDelete = isDeleteVisible ? isDeleteVisible(rowData) : true;

            return [
              ...(isDeleted
                ? []
                : [
                    ...(showEdit ? [
                      <MenuItem
                        key="edit"
                        onClick={() => {
                          handleEditRow(rowData.id);
                          closeMenu();
                        }}
                      >
                        <ListItemIcon>
                          <EditIcon />
                        </ListItemIcon>
                        {langStore.editCommand}
                      </MenuItem>,
                    ] : []),
                    ...(showDelete ? [
                      <MenuItem
                        key="delete"
                        onClick={() => {
                          handleDeleteRow(rowData.id);
                          closeMenu();
                        }}
                      >
                        <ListItemIcon>
                          <DeleteIcon />
                        </ListItemIcon>
                        {langStore.deleteCommand}
                      </MenuItem>,
                    ] : []),
                  ]),

              ...menuActions.map((action, index) => {
                const isEnabled = action.isActionEnabled
                  ? action.isActionEnabled(rowData)
                  : true;

                return (
                  <MenuItem
                    key={`action-${index}`}
                    disabled={!isEnabled}
                    onClick={(e) => {
                      action.onClick(rowData, e);
                      closeMenu();
                    }}
                  >
                    <ListItemIcon>{action.icon}</ListItemIcon>
                    {action.label}
                  </MenuItem>
                );
              }),

              ...(isDeleted
                ? [
                    <MenuItem
                      key="restore"
                      onClick={() => {
                        onRestore(rowData.id);
                        closeMenu();
                      }}
                    >
                      <ListItemIcon>
                        <RestoreIcon />
                      </ListItemIcon>
                      {langStore.restoreCommand}
                    </MenuItem>,
                  ]
                : []),
            ];
          }}
        />
    );

    return (
      <Box>
        {showLabel && (
          <Box sx={{ marginLeft: "10px", marginBottom: "8px" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 500 }} component="div">
              <FieldLabelWithHelp label={`${label}:`} description={description} />
            </Typography>
          </Box>
        )}

        {isFullScreen ? (
          <Dialog open fullWidth maxWidth="xl" onClose={() => setIsFullScreen(false)} sx={{ zIndex: 10100 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1, borderBottom: 1, borderColor: "divider" }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{label}</Typography>
              <Tooltip title={langStore.minimizeCommand}>
                <IconButton onClick={() => setIsFullScreen(false)} size="small" aria-label="minimize">
                  <FullscreenExitIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <DialogContent>
              {gridContent}
            </DialogContent>
          </Dialog>
        ) : gridContent}

        {(error || helperText) && (
          <Typography
            color="error"
            variant="caption"
            sx={{ marginLeft: "14px", marginTop: "3px", display: "block" }}
          >
            {error || helperText}
          </Typography>
        )}

        {popupComponent && editingRow && isPopupOpen && (
          <Dialog
            open={true}
            disableEscapeKeyDown
            fullWidth={popupMaxWidth !== "lg" && popupMaxWidth !== "xl"}
            maxWidth={popupMaxWidth === "lg" || popupMaxWidth === "xl" ? false : popupMaxWidth}
            onClose={handleCancelChanges}
            sx={{ zIndex: 10200 }}
            PaperProps={
              popupMaxWidth === "lg" || popupMaxWidth === "xl"
                ? { sx: { width: "min(1400px, 90vw)", maxWidth: "90vw" } }
                : undefined
            }
          >
            <DialogContent>
              {popupComponent(editingRow)}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelChanges} color="secondary">
                {langStore.cancelCommand}
              </Button>
              <Button onClick={handleApplyChanges} color="primary">
                {"OK"}
              </Button>
            </DialogActions>
          </Dialog>
        )}
        {excelViewerElement}
      </Box>
    );
  }
);
